<template>
  <div
    class="two-column-video-block critical-component-clear"
  >
    <div
      class="two-column-video-block__container"
      :class="`two-column-video-block__container__${data.content_alignment}`"
      :style="`--pointer-state: ${ !data.video_id ? 'none' : 'auto' };`"
    >
      <h2
        v-if="data.title && isMobile && data.mobile_title_position"
        class="
          text-heading-2xl
          text-heading-3xl-desktop
          two-column-video-block__title
          two-column-video-block__title-mobile-top
        "
        v-text="data.title"
      />
      <div
        v-if="data.video_id"
        class="two-column-video-block__video-controller-container"
      >
        <iframe
          width="100%"
          height="100%"
          :src="`https://www.youtube.com/embed/${data.video_id}?autoplay=${isVideoPlaying ? 1 : 0}&mute=1`"
          frameborder="0"
          class="two-column-video-block__video"
          allow="
            accelerometer;
            autoplay;
            clipboard-write;
            encrypted-media;
            gyroscope;
            picture-in-picture;
            web-share
          "
          allowfullscreen
        />
      </div>
      <div
        v-else
        class="two-column-video-block__video-controller-container"
      >
        <responsive-image
          v-if="data.two_col_video_fallback?.src"
          class="two-column-video-block__video-fallback"
          :image="data.two_col_video_fallback"
          :lazy-load="false"
          :padding="false"
          min-max="350-1200"
          sizes="(min-width: 1024px) 99px, 95px"
        />
        <video-player
          ref="video"
          :key="activeVideo"
          class="two-column-video-block__video two-column-video-block__video-player"
          :class="`two-column-video-block__video__${data.content_alignment}`"
          :video="activeVideo"
          :show-controls="false"
        />
        <div
          v-if="!data.video_id"
          class="two-column-video-block__video-controls"
          :class="`two-column-video-block__video-controls__${data.content_alignment}`"
        >
          <button
            type="button"
            :class="{ 'disabled': isVideoPlaying }"
            :aria-label="$string('accessibility.video.play')"
            @click="toggleVideo"
          >
            <icon-play />
          </button>
          <button
            type="button"
            :class="{ 'disabled': !isVideoPlaying }"
            :aria-label="$string('accessibility.video.pause')"
            @click="toggleVideo"
          >
            <icon-pause />
          </button>
          <button
            type="button"
            :aria-label="$string('accessibility.video.full_screen')"
            @click="toggleFullScreen"
          >
            <icon-full-screen />
          </button>
        </div>
      </div>
      <div
        class="two-column-video-block__content"
      >
        <h2
          v-if="data.title"
          :style="isMobile && data.mobile_title_position ? 'display: none;' : 'display: block;'"
          class="
            text-heading-2xl
            text-heading-3xl-desktop
            two-column-video-block__title
          "
          v-text="data.title"
        />
        <p
          v-if="data.text"
          class="
            text-body-s-regular
            two-column-video-block__text
          "
          v-html="data.text"
        />
        <btn
          v-if="data.cta_url && data.cta_text"
          class="two-column-video-block__cta text-other-button-s"
          :label="data.cta_text"
          :target="data.new_tab ? '_blank' : ''"
          :url="data.cta_url"
          :modifiers="`primary ${buttonStyle}`"
        />
      </div>
    </div>

    <!-- Video full screen modal -->
    <overlay
      :key="twoColumnVideoId"
      block-class="two-column-video-block__modal"
      direction="up"
      :namespace="twoColumnVideoId"
      type="modal"
    >
      <template #body="overlay">
        <responsive-image
          v-if="data.two_col_video_fallback?.src"
          class="two-column-video-block__video-fallback"
          :image="data.two_col_video_fallback"
          :lazy-load="false"
          :padding="false"
          min-max="350-1200"
          sizes="(min-width: 1024px) 99px, 95px"
        />
        <iframe
          v-if="data.video_id"
          width="100%"
          height="100%"
          :src="`https://www.youtube.com/embed/${data.video_id}`"
          frameborder="0"
          class="two-column-video-block__video"
          allow="
            accelerometer;
            autoplay;
            clipboard-write;
            encrypted-media;
            gyroscope;
            picture-in-picture;
            web-share
          "
        />
        <video-player
          v-else
          ref="videoModal"
          :key="activeVideo"
          class="two-column-video-block__video-modal"
          :video="activeVideo"
          show-controls
          :autoplay="false"
          :overlay="overlay"
        />
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: two-column-video-block (two-column-video-block)
 * -----------------------------------------------------------------------------
 * This a video block component.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 *
 */

import { mapActions, mapGetters, mapState } from 'vuex'

import ResponsiveImage from '~global/images/responsive-image'
import Btn from '~global/btn/btn'
import Overlay from '~global/overlay/overlay'
import VideoPlayer from '~async/video-player/video-player'

import IconPause from '~icons/general/pause.svg'
import IconPlay from '~icons/general/play.svg'
import IconFullScreen from '~icons/general/full-screen.svg'

export default {
  name: 'TwoColumnVideoBlock',

  components: {
    ResponsiveImage,
    VideoPlayer,
    Btn,
    IconPause,
    IconPlay,
    IconFullScreen,
    Overlay,
  },

  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      isVideoPlaying: false,
      twoColumnVideoId: `tcv-${crypto.randomUUID()}-${Date.now()}`,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Returns button color style.
     */
    buttonStyle() {
      if (this.data.text_colour === 'light') {
        return 'dark'
      }

      return 'light'
    },

    /**
     * Returns active video source.
     */
    activeVideo() {
      if (this.isMobile && this.data.two_col_video_mobile) {
        return this.getVideoSource(this.data.two_col_video_mobile.sources)
      }

      if (this.data.two_col_video_desktop) {
        return this.getVideoSource(this.data.two_col_video_desktop.sources)
      }

      return false
    },
  },

  watch: {

    /**
     * Watch active overlay to reset current link.
     */
    async getActiveOverlay() {
      await this.$nextTick()

      if (this.getActiveOverlay || this.data.video_id) {
        return
      }

      this.$refs.videoModal.pauseVideo()
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Handle video toggle.
     */
    toggleVideo() {
      if (this.data.video_id) {
        this.isVideoPlaying = !this.isVideoPlaying

        return
      }

      if (this.isVideoPlaying) {
        this.$refs.video.pauseVideo()
        this.isVideoPlaying = false
        return
      }

      this.$refs.video.playVideo()
      this.isVideoPlaying = true
    },

    /**
     * Handle full screen toggle.
     */
    toggleFullScreen() {
      this.toggleOverlay({
        ignoreDismissed: true,
        namespace: this.twoColumnVideoId,
      })

      if (this.data.video_id) {
        this.isVideoPlaying = false

        return
      }

      this.$nextTick().then(() => {
        this.$refs.video.pauseVideo()
      })
    },

    /**
     * Get video MP4 source object.
     * @param {Array} sources - Video sources.
     * @returns {Object}
     */
    getVideoSource(sources) {
      return sources.find((source) => source.format === 'mp4')
    },
  },
}
</script>

<style lang="scss">
@import './two-column-video-block';
</style>
